<template>
	<section class="page page-place">
		<!-- Venue Not Found -->
		<ViewVenueNotFound
			v-bind:showNearby="$feature('venue_nearby')"
			v-bind:venueCollection="nearbyVenueCollection"
			v-bind:venueModel="venueModel"
			v-if="notExists"
			v-on:venue:select="Handle_OnSelectVenue"
		/>

		<!-- Main Content Scene -->
		<ScenePlaceContent
			ref="content"
			v-bind:nearbyVenueCollection="nearbyVenueCollection"
			v-bind:venueModel="venueModel"
			v-else-if="venueModel.uniqueKey"
			v-on:play="Handle_OnClickPlay"
			v-on:submit:media="Handle_OnRequestSubmitMedia"
			v-on:user:select="Handle_OnSelectUser"
			v-on:venue:select="Handle_OnSelectVenue"
		/>

		<!-- Helpers -->
		<section class="utility">
			<ChalkyMediaImageUploader
				v-bind:canChangeVenue="false"
				v-bind:canChangeUser="false"
				v-bind:defaultVenueModel="venueModel"
				v-bind:showDescription="false"
				v-bind:subgroups="['gallery']"
				v-if="shouldShowImageUploader"
				v-model="shouldShowImageUploader"
			/>
		</section>
	</section>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import PageBase from '@/Core/Page.vue';
	import { Component, Ref, Watch } from 'vue-property-decorator';
	import { beforeDestroy, mounted } from '@/Utility/Decorators';

	/**
	 * Page controller for displaying venue/place details. Handles data fetching,
	 * modal management, and route navigation while delegating content display
	 * to ScenePlaceContent.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Page
	 * @project ChalkySticks App
	 */
	@Component
	export default class PagePlace extends PageBase {
		/**
		 * Reference to the content scene
		 *
		 * @type any
		 */
		@Ref('content')
		readonly content!: any;

		/**
		 * @type boolean
		 */
		protected notExists: boolean = false;

		/**
		 * @type ChalkySticks.Collection.Venue
		 */
		protected nearbyVenueCollection: ChalkySticks.Collection.Venue = ChalkySticks.Factory.Venue.collection({
			limit: 8,
		});

		/**
		 * @type boolean
		 */
		protected shouldShowImageUploader: boolean = false;

		/**
		 * @type boolean
		 */
		protected shouldShowVideoUploader: boolean = false;

		/**
		 * @type ChalkySticks.Model.Venue
		 */
		protected venueModel: ChalkySticks.Model.Venue = ChalkySticks.Factory.Venue.model();

		/**
		 * @return void
		 */
		@mounted
		public attachEvents(): void {
			ChalkySticks.Core.Event.Bus.on('scan-table:request', this.Handle_OnClickScanTable);
			ChalkySticks.Core.Event.Bus.on('checkin:complete', this.Handle_OnCheckinComplete);
		}

		/**
		 * @return void
		 */
		@beforeDestroy
		public detachEvents(): void {
			ChalkySticks.Core.Event.Bus.off('scan-table:request', this.Handle_OnClickScanTable);
			ChalkySticks.Core.Event.Bus.off('checkin:complete', this.Handle_OnCheckinComplete);
		}

		/**
		 * @return Promise<void>
		 */
		@mounted
		protected async setup(): Promise<void> {
			await this.fetchVenueData();

			// Get tagged media
			// await this.venueModel.taggedMedia.fetch();
		}

		/**
		 * Fetch venue data based on current route parameter
		 * @return Promise<void>
		 */
		protected async fetchVenueData(): Promise<void> {
			const navigatingModel = this.$store.getters.navigatingVenueModel;

			// Use navigating model from store if available (in-app navigation)
			if (navigatingModel && navigatingModel.getSlug() === this.$route.params.id) {
				this.venueModel = navigatingModel;
			} else {
				this.venueModel.set({
					id: this.$route.params.id,
				});
			}

			// Fetch venue model
			try {
				await this.venueModel.fetch();
			} catch (e: any) {
				this.notExists = true;
			}

			// Redirect to 404
			if (!this.venueModel?.id) {
				this.$router.push({
					path: '/not-found',
				});

				return;
			}

			// Update query parameters
			this.nearbyVenueCollection.setQueryParams({
				latitude: this.venueModel.getLatitude(),
				longitude: this.venueModel.getLongitude(),
			});

			// Fetch venues that are nearby
			this.nearbyVenueCollection.fetch();
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * Handle successful checkin from the global checkin modal. Runs the
		 * page-level side effects (analytics + scroll) only when the completed
		 * venue matches the current page's venue.
		 *
		 * @param ChalkySticks.Core.IDispatcherEvent<{ venueModel: ChalkySticks.Model.Venue }> e
		 * @return Promise<void>
		 */
		protected async Handle_OnCheckinComplete(
			e: ChalkySticks.Core.IDispatcherEvent<{ venueModel: ChalkySticks.Model.Venue }>,
		): Promise<void> {
			const completedVenueModel = e?.data?.venueModel;

			// Ignore checkins for other venues
			if (!completedVenueModel || completedVenueModel.id !== this.venueModel.id) {
				return;
			}

			// Track event
			ChalkySticks.Utility.Analytics.track('place:scan.checkin', {
				venueId: this.venueModel.id,
				venueName: this.venueModel.getName(),
			});

			// Scroll to the checkins section via content scene
			await this.$nextTick();
			this.content?.scrollToCheckins();
		}

		/**
		 * Called when the game component is ready, sends rack positions to the game
		 *
		 * @param MouseEvent e
		 * @return void
		 */
		protected Handle_OnGameReady(e: MouseEvent): void {
			const inverseRackPositions = this.content?.getInverseRackPositions();

			ChalkySticks.Core.Event.Bus.dispatch('game.control:rackCustom', inverseRackPositions);
		}

		/**
		 * Trigger the global checkin modal pre-seeded with the current venue so
		 * all scanning goes through the shared flow.
		 *
		 * @param ChalkySticks.Core.IDispatcherEvent<any> e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickScanTable(e: ChalkySticks.Core.IDispatcherEvent<any>): Promise<void> {
			ChalkySticks.Utility.Analytics.track('place:scan.open');

			ChalkySticks.Core.Event.Bus.dispatch('modal:checkin:open', {
				venueModel: this.venueModel,
			});
		}

		/**
		 * @return Promise<void>
		 */
		protected async Handle_OnRequestSubmitMedia(): Promise<void> {
			this.shouldShowImageUploader = true;
		}

		/**
		 * Watch for route parameter changes to refetch venue data
		 * @param string newId
		 * @param string oldId
		 * @return Promise<void>
		 */
		@Watch('$route.params.id')
		protected async Handle_OnRouteChange(newId: string, oldId: string): Promise<void> {
			if (newId && newId !== oldId) {
				await this.fetchVenueData();

				// scroll to top
				this.$nextTick(() => this.$el.scrollTo(0, 0));
			}
		}

		/**
		 * @param ChalkySticks.Model.User userModel
		 * @return Promise<void>
		 */
		protected async Handle_OnSelectUser(userModel: ChalkySticks.Model.User): Promise<void> {
			this.$store.dispatch('gotoUser', userModel);
		}

		/**
		 * Navigate to selected venue page
		 * @param ChalkySticks.Model.Venue venueModel
		 * @return Promise<void>
		 */
		protected async Handle_OnSelectVenue(venueModel: ChalkySticks.Model.Venue): Promise<void> {
			this.$store.dispatch('gotoVenue', venueModel);
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.page-place {
		background-color: var(--chalky-blue-4);

		> .content-container {
			position: relative;

			.venue-formatteddetails:not(.has-details) {
				display: none;
			}

			> .view-venue-revision {
				// mk: So the modal can sit above everything else
				position: initial;
			}
		}

		.venue-gallery {
			filter: blur(0);
			position: sticky;
			top: -100px;
			transition: height 0.5s var(--ease-in-out-cubic), filter 0.75s ease, opacity 0.5s ease, transform 0.5s ease;
			transform-origin: top center;
			z-index: 0;

			figcaption {
				transition: opacity 0.3s ease;
			}
		}

		&.has-scrolled .venue-gallery {
			filter: blur(20px);
			transform: translateY(-0px);
			opacity: 0.75;

			figcaption {
				opacity: 0;
			}
		}
	}

	// State
	// ---------------------------------------------------------------------------

	.page-place.expanded-gallery {
		.venue-gallery {
			height: calc(100svh - var(--global-tab-height));
		}
	}
</style>
